import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { GridTileImage } from '../grid/tile';
import type { Product } from 'lib/burokshop/types';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Kiosk {
  id: string;
  business_name: string;
  logo_url: string | null;
}

interface KioskCarouselProps {
  kiosk: Kiosk;
}

export function KioskCarousel({ kiosk }: KioskCarouselProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowingAnimating, setIsFollowingAnimating] = useState(false);

  useEffect(() => {
    async function loadProducts() {
      try {
        const { data, error } = await supabase
          .from('kiosk_products')
          .select('*')
          .eq('kiosk_id', kiosk.id)
          .eq('is_active', true)
          .order('updated_at', { ascending: false })
          .limit(10);

        if (error) throw error;

        if (data) {
          // Mapper les produits depuis Supabase vers le format Product
          const mappedProducts: Product[] = data.map((row: any) => {
            const images = Array.isArray(row.images) ? row.images : [];
            const firstImage = images[0];
            const featuredImage = firstImage ? {
              url: typeof firstImage === 'string' ? firstImage : firstImage.url || firstImage,
              altText: row.name,
              width: 800,
              height: 800
            } : {
              url: '',
              altText: row.name,
              width: 800,
              height: 800
            };

            return {
              id: row.id,
              handle: row.name.toLowerCase().replace(/\s+/g, '-') + '-' + row.id,
              availableForSale: (row.stock_quantity ?? 0) > 0,
              title: row.name,
              description: row.description || '',
              descriptionHtml: row.description || '',
              options: [],
              priceRange: {
                maxVariantPrice: {
                  amount: (row.original_price || row.price).toString(),
                  currencyCode: 'XOF'
                },
                minVariantPrice: {
                  amount: row.price.toString(),
                  currencyCode: 'XOF'
                }
              },
              variants: [{
                id: row.id,
                title: 'Default',
                availableForSale: (row.stock_quantity ?? 0) > 0,
                selectedOptions: [],
                price: {
                  amount: row.price.toString(),
                  currencyCode: 'XOF'
                }
              }],
              featuredImage,
              images: images.map((img: any, index: number) => ({
                url: typeof img === 'string' ? img : img.url || img,
                altText: `${row.name} - ${index + 1}`,
                width: 800,
                height: 800
              })),
              seo: {
                title: row.name,
                description: row.description || row.name
              },
              tags: row.tags || [],
              updatedAt: row.updated_at || row.created_at
            };
          });

          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error('Error loading kiosk products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, [kiosk.id]);

  if (loading) {
    return (
      <div className="py-8">
        <div className="h-4 w-48 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
      </div>
    );
  }

  if (!products.length) return null;

  // Dupliquer les produits pour le carrousel infini
  const carouselProducts = [...products, ...products, ...products];

  return (
    <div className="mb-12">
      {/* En-tête du kiosk */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <Link 
          to={`/kiosk/${kiosk.id}`}
          className="flex items-center gap-4 group"
        >
          {kiosk.logo_url && (
            <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full ring-2 ring-neutral-200 dark:ring-neutral-700 transition-all duration-200 group-hover:ring-blue-400 group-hover:scale-105">
              <img
                src={kiosk.logo_url}
                alt={kiosk.business_name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white transition-colors duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">
              {kiosk.business_name}
            </h2>
            <div className="flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-400">
              <span className="font-semibold">4,8</span>
              <span className="text-black dark:text-white">★</span>
              <span className="text-neutral-500">(10,3)</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setIsFollowingAnimating(true);
              setTimeout(() => {
                setIsFollowing(!isFollowing);
                setIsFollowingAnimating(false);
              }, 300);
            }}
            className={`relative flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ${
              isFollowing
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                : 'bg-white border-2 border-neutral-300 text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
            } ${isFollowingAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
          >
            {isFollowing ? (
              <>
                <HeartIconSolid className="h-4 w-4 animate-pulse" />
                <span>Suivi</span>
              </>
            ) : (
              <>
                <HeartIcon className="h-4 w-4" />
                <span>Suivre</span>
              </>
            )}
            {isFollowingAnimating && (
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
            )}
          </button>
          <Link
            to={`/kiosk/${kiosk.id}`}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-2 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
          >
            <ShoppingBagIcon className="h-4 w-4" />
            Visiter
          </Link>
        </div>
      </div>

      {/* Carrousel de produits */}
      <div className="w-full overflow-hidden pb-6 pt-1">
        <ul className="flex animate-carousel gap-4">
          {carouselProducts.map((product, i) => (
            <li
              key={`${product.handle}${i}`}
              className="relative aspect-square h-[30vh] max-h-[275px] w-2/3 max-w-[475px] flex-none md:w-1/3"
            >
              <Link to={`/product/${product.handle}`} className="relative h-full w-full">
                <GridTileImage
                  alt={product.title}
                  label={{
                    title: product.title,
                    amount: product.priceRange.maxVariantPrice.amount,
                    currencyCode: product.priceRange.maxVariantPrice.currencyCode
                  }}
                  src={product.featuredImage?.url}
                  fill
                  sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

